// ---------------------------------------------------------------------------
// C1: Create Contact Modal
// ---------------------------------------------------------------------------
// Fields: first name, last name, email (required), phone, company, tags,
// source dropdown, notes. POST /v1/people. Toast on success. Design system.
// ---------------------------------------------------------------------------
import { useState, useId, type FormEvent } from "react";
import { X, Check, AlertCircle, UserPlus } from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CreateContactData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  tags: string[];
  source: string;
  notes: string;
}

interface CreateContactModalProps {
  onSubmit: (data: CreateContactData) => Promise<void>;
  onClose: () => void;
  isSubmitting?: boolean;
}

// ---------------------------------------------------------------------------
// Source Options
// ---------------------------------------------------------------------------

const SOURCE_OPTIONS = [
  { value: "", label: "Select source..." },
  { value: "google_ads", label: "Google Ads" },
  { value: "referral", label: "Referral" },
  { value: "website", label: "Website" },
  { value: "social", label: "Social Media" },
  { value: "manual", label: "Manual Entry" },
  { value: "other", label: "Other" },
];

// ---------------------------------------------------------------------------
// Email validation
// ---------------------------------------------------------------------------

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CreateContactModal({ onSubmit, onClose, isSubmitting = false }: CreateContactModalProps) {
  const formId = useId();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [tagsInput, setTagsInput] = useState("");
  const [source, setSource] = useState("");
  const [notes, setNotes] = useState("");

  const [emailTouched, setEmailTouched] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [shakeField, setShakeField] = useState<string | null>(null);

  const isEmailValid = EMAIL_REGEX.test(email);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!isEmailValid) {
      newErrors.email = "Invalid email address";
    }
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      // Shake the first invalid field
      const firstKey = Object.keys(newErrors)[0]!;
      setShakeField(firstKey);
      setTimeout(() => setShakeField(null), 300);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate() || isSubmitting) return;

    const tags = tagsInput
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);

    await onSubmit({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      phone: phone.trim(),
      company: company.trim(),
      tags,
      source,
      notes: notes.trim(),
    });
  };

  const fieldClass = (name: string) =>
    `w-full rounded-lg border px-3 py-2 text-sm text-foreground bg-background transition-all
    focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20 focus:border-[var(--color-primary)]
    ${errors[name] ? "border-[var(--color-destructive)] focus:ring-[var(--color-destructive)]/20 focus:border-[var(--color-destructive)]" : "border-border"}
    ${shakeField === name ? "animate-[shake_200ms_ease-in-out]" : ""}`;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[4px] transition-opacity duration-250"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="w-full max-w-lg rounded-xl border border-border bg-background shadow-xl animate-in zoom-in-95 fade-in duration-250"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-border px-6 py-4">
            <div className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-[var(--color-primary)]" />
              <h2 className="text-lg font-semibold text-foreground">Create Contact</h2>
            </div>
            <button
              onClick={onClose}
              className="rounded-lg p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Form */}
          <form id={formId} onSubmit={handleSubmit} className="space-y-4 px-6 py-4">
            {/* Name row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">First Name</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className={fieldClass("firstName")}
                  placeholder="John"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Last Name</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className={fieldClass("lastName")}
                  placeholder="Doe"
                />
              </div>
            </div>

            {/* Email (required) */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Email <span className="text-[var(--color-destructive)]">*</span>
              </label>
              <div className="relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setErrors((p) => { const n = { ...p }; delete n.email; return n; }); }}
                  onBlur={() => setEmailTouched(true)}
                  className={fieldClass("email")}
                  placeholder="john@example.com"
                  required
                />
                {emailTouched && email && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {isEmailValid ? (
                      <Check className="h-4 w-4 text-[var(--color-success)]" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-[var(--color-destructive)]" />
                    )}
                  </div>
                )}
              </div>
              {errors.email && (
                <p className="mt-1 text-xs text-[var(--color-destructive)] animate-in slide-in-from-top-1 duration-150">
                  {errors.email}
                </p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Phone</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className={fieldClass("phone")}
                placeholder="+1 (555) 000-0000"
              />
            </div>

            {/* Company */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Company</label>
              <input
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className={fieldClass("company")}
                placeholder="Acme Corp"
              />
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Tags</label>
              <input
                type="text"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                className={fieldClass("tags")}
                placeholder="vip, enterprise, nurture (comma-separated)"
              />
            </div>

            {/* Source */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Source</label>
              <select
                value={source}
                onChange={(e) => setSource(e.target.value)}
                className={fieldClass("source")}
              >
                {SOURCE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className={`${fieldClass("notes")} resize-none`}
                rows={3}
                placeholder="Any additional context..."
              />
            </div>
          </form>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 border-t border-border px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              form={formId}
              disabled={isSubmitting}
              className="btn-press rounded-lg px-5 py-2 text-sm font-medium text-white transition-colors disabled:opacity-50"
              style={{ background: "var(--gradient-energy)" }}
            >
              {isSubmitting ? "Creating..." : "Create Contact"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
